import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import {
  HotspotPassenger,
  HotspotPassengerBulkSyncResponse,
  HotspotPassengerSyncResult,
  HotspotSessionRelinkResponse,
  HotspotSessionRemapResponse,
  HotspotSessionSyncAllResponse,
  HotspotSessionSyncError,
  HotspotSessionSyncResponse,
  HotspotSessionSyncResultRow,
  HotspotSyncRun
} from '../../core/models/hotspot-passenger.model';
import { AuthService } from '../../core/services/auth.service';
import { HotspotPassengerService } from '../../core/services/hotspot-passenger.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-hotspot-passengers',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ConfirmDialogComponent],
  templateUrl: './hotspot-passengers.component.html',
  styleUrls: ['./hotspot-passengers.component.css']
})
export class HotspotPassengersComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private hotspotPassengerService = inject(HotspotPassengerService);

  passengers: HotspotPassenger[] = [];
  filteredPassengers: HotspotPassenger[] = [];
  selectedPassenger: HotspotPassenger | null = null;
  searchTerm = '';
  selectedPassengerId: string | null = null;
  isLoadingPassengers = false;
  passengerListError = '';
  passengerPage = 1;
  passengerPageSize = 5;

  syncResults: Record<string, HotspotPassengerSyncResult> = {};
  syncErrors: Record<string, string> = {};
  syncingIds = new Set<string>();
  successMessage = '';
  activeSyncTab: 'passengers' | 'sessions' | 'maintenance' | 'history' = 'passengers';
  passengerSyncPage = 1;
  passengerSyncPerPage = 100;
  passengerSyncSearch = '';
  passengerSyncSortBy = '';
  passengerSyncSortDirection: 'asc' | 'desc' = 'desc';
  isPassengerBatchSyncing = false;
  passengerBatchSyncResult: HotspotPassengerBulkSyncResponse | null = null;
  passengerBatchSyncError = '';
  showPassengerBatchErrors = false;
  sessionLimit = 500;
  sessionOffset = 0;
  isSessionSyncing = false;
  sessionSyncResult: HotspotSessionSyncResponse | null = null;
  sessionResultPage = 1;
  sessionResultPageSize = 5;
  sessionSyncError = '';
  showSessionErrors = false;
  catchUpLimit = 500;
  catchUpStartOffset = 0;
  catchUpMaxBatches: number | null = null;
  isCatchUpSyncing = false;
  catchUpResult: HotspotSessionSyncAllResponse | null = null;
  catchUpError = '';
  showCatchUpErrors = false;
  maintenanceLimit = 1000;
  readonly maintenanceLimitOptions = [1000, 2500, 5000];
  isRemapping = false;
  remapResult: HotspotSessionRemapResponse | null = null;
  remapError = '';
  showRemapErrors = false;
  isRelinking = false;
  relinkResult: HotspotSessionRelinkResponse | null = null;
  relinkError = '';
  showRelinkErrors = false;
  sessionHistory: HotspotSyncRun[] = [];
  isLoadingSessionHistory = false;
  sessionHistoryError = '';
  sessionHistoryPage = 1;
  sessionHistoryPerPage = 5;
  sessionHistoryTotalPages = 1;
  sessionHistoryResource: '' | 'Passengers' | 'Sessions' = '';
  isAdmin = false;
  isAdminOrPlatform = false;
  pendingHotspotAction: {
    type: 'catchUp' | 'remap' | 'relink';
    title: string;
    message: string;
    confirmLabel: string;
  } | null = null;

  ngOnInit(): void {
    const role = this.getCurrentRole();
    this.isAdmin = role === UserRole.ADMIN;
    this.isAdminOrPlatform = role === UserRole.ADMIN || role === UserRole.PLATFORM_EMPLOYEE;
    this.loadSessionHistory();

    this.route.paramMap.subscribe(params => {
      this.selectedPassengerId = params.get('id');
      this.selectPassengerFromLoadedList();
    });

    this.loadPassengers();
  }

  loadPassengers(): void {
    this.isLoadingPassengers = true;
    this.passengerListError = '';

    this.hotspotPassengerService.getHotspotPassengers().pipe(
      finalize(() => this.isLoadingPassengers = false)
    ).subscribe({
      next: passengers => {
        this.passengers = passengers;
        this.applyFilters();
        this.selectPassengerFromLoadedList();
      },
      error: error => {
        this.passengers = [];
        this.filteredPassengers = [];
        this.selectedPassenger = null;
        this.passengerListError = this.getErrorMessage(error) || 'Unable to load hotspot passengers.';
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    const filtered = term
      ? this.passengers.filter(passenger => [
          passenger.displayName,
          passenger.phone,
          passenger.email,
          passenger.provider,
          passenger.externalId,
          passenger.packageName,
          passenger.profileId,
          passenger.status,
          passenger.onlineState
        ].some(value => String(value || '').toLowerCase().includes(term)))
      : [...this.passengers];

    this.filteredPassengers = filtered.sort((first, second) => this.comparePassengersByPresence(first, second));
    this.passengerPage = Math.min(this.passengerPage, this.getPassengerTotalPages());
  }

  onPassengerSearchChanged(): void {
    this.passengerPage = 1;
    this.applyFilters();
  }

  getPagedPassengers(): HotspotPassenger[] {
    const start = (this.passengerPage - 1) * this.passengerPageSize;
    return this.filteredPassengers.slice(start, start + this.passengerPageSize);
  }

  getPassengerTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredPassengers.length / this.passengerPageSize));
  }

  getPassengerPageSummary(): string {
    if (!this.filteredPassengers.length) {
      return '0 shown';
    }

    const start = ((this.passengerPage - 1) * this.passengerPageSize) + 1;
    const end = Math.min(this.passengerPage * this.passengerPageSize, this.filteredPassengers.length);
    return `${start}-${end} of ${this.filteredPassengers.length} shown`;
  }

  changePassengerPage(page: number): void {
    const totalPages = this.getPassengerTotalPages();
    if (page < 1 || page > totalPages || page === this.passengerPage) {
      return;
    }

    this.passengerPage = page;
  }

  openPassenger(passenger: HotspotPassenger): void {
    this.router.navigate(['/dashboard/hotspot/passengers', passenger.id]);
  }

  syncProfile(passenger: HotspotPassenger, event?: Event): void {
    event?.stopPropagation();

    if (this.isSyncing(passenger.id)) {
      return;
    }

    this.successMessage = '';
    delete this.syncErrors[passenger.id];
    this.syncingIds.add(passenger.id);

    this.hotspotPassengerService.syncPassengerProfile(passenger.id).pipe(
      finalize(() => this.syncingIds.delete(passenger.id))
    ).subscribe({
      next: result => {
        this.syncResults[passenger.id] = result;
        passenger.profileId = result.profile.id;
        passenger.lastSynced = result.externalIdentity.lastSynced || new Date().toISOString();
        this.successMessage = this.getSyncMessage(result);
      },
      error: error => {
        this.syncErrors[passenger.id] = this.getErrorMessage(error);
      }
    });
  }

  isSyncing(id: string): boolean {
    return this.syncingIds.has(id);
  }

  setSyncTab(tab: 'passengers' | 'sessions' | 'maintenance' | 'history'): void {
    this.activeSyncTab = tab;
  }

  syncPassengerProfiles(): void {
    if (!this.isAdminOrPlatform || this.isPassengerBatchSyncing) {
      return;
    }

    this.passengerBatchSyncError = '';
    this.passengerBatchSyncResult = null;
    this.showPassengerBatchErrors = false;
    this.successMessage = '';
    this.isPassengerBatchSyncing = true;

    this.hotspotPassengerService.syncPassengerProfiles({
      page: this.normalizedPassengerSyncPage(),
      perPage: this.normalizedPassengerSyncPerPage(),
      search: this.passengerSyncSearch,
      sortBy: this.passengerSyncSortBy,
      sortDirection: this.passengerSyncSortDirection
    }).pipe(
      finalize(() => this.isPassengerBatchSyncing = false)
    ).subscribe({
      next: result => {
        this.passengerBatchSyncResult = result;
        this.successMessage = `Processed ${result.processed} Powerlynx customers and created ${result.createdProfiles} profiles.`;
        this.loadPassengers();
        this.loadSessionHistory(1, 'Passengers');
      },
      error: error => {
        this.passengerBatchSyncError = this.getErrorMessage(error) || 'Unable to sync Powerlynx customers.';
      }
    });
  }

  syncSessions(): void {
    if (!this.isAdminOrPlatform || this.isSessionSyncing) {
      return;
    }

    this.sessionSyncError = '';
    this.sessionSyncResult = null;
    this.sessionResultPage = 1;
    this.showSessionErrors = false;
    this.isSessionSyncing = true;

    this.hotspotPassengerService.syncSessions(this.normalizedLimit(), this.normalizedOffset()).pipe(
      finalize(() => this.isSessionSyncing = false)
    ).subscribe({
      next: result => {
        this.sessionSyncResult = result;
        this.sessionResultPage = 1;
        this.loadSessionHistory(1, 'Sessions');
      },
      error: error => {
        this.sessionSyncError = this.getErrorMessage(error) || 'Unable to sync hotspot sessions.';
      }
    });
  }

  syncAllSessions(): void {
    if (!this.isAdminOrPlatform || this.isCatchUpSyncing) {
      return;
    }

    const maxBatches = this.normalizedCatchUpMaxBatches();
    if (!maxBatches) {
      this.pendingHotspotAction = {
        type: 'catchUp',
        title: 'Run session catch-up',
        message: 'Run session catch-up without a max batch limit? This may continue until Powerlynx returns no more sessions.',
        confirmLabel: 'Run catch-up'
      };
      return;
    }

    this.runSessionCatchUp(maxBatches);
  }

  cancelHotspotAction(): void {
    this.pendingHotspotAction = null;
  }

  confirmHotspotAction(): void {
    const action = this.pendingHotspotAction?.type;
    this.pendingHotspotAction = null;

    if (action === 'catchUp') {
      this.runSessionCatchUp(this.normalizedCatchUpMaxBatches());
      return;
    }

    if (action === 'remap') {
      this.runRemapExistingSessions();
      return;
    }

    if (action === 'relink') {
      this.runRelinkPassengerProfiles();
    }
  }

  private runSessionCatchUp(maxBatches: number | null): void {
    this.catchUpError = '';
    this.catchUpResult = null;
    this.showCatchUpErrors = false;
    this.successMessage = '';
    this.isCatchUpSyncing = true;

    this.hotspotPassengerService.syncAllSessions(
      this.normalizedCatchUpLimit(),
      this.normalizedCatchUpStartOffset(),
      maxBatches
    ).pipe(
      finalize(() => this.isCatchUpSyncing = false)
    ).subscribe({
      next: result => {
        this.catchUpResult = result;
        this.successMessage = `Session catch-up processed ${result.processed} records across ${result.batchesProcessed} batches.`;
        this.loadSessionHistory(1, 'Sessions');
      },
      error: error => {
        this.catchUpError = this.getErrorMessage(error) || 'Unable to run session catch-up.';
      }
    });
  }

  remapExistingSessions(): void {
    if (!this.isAdminOrPlatform || this.isRemapping) {
      return;
    }

    this.pendingHotspotAction = {
      type: 'remap',
      title: 'Remap existing sessions',
      message: 'Remap existing sessions now? This re-resolves organisation and vehicle links using current Powerlynx mappings.',
      confirmLabel: 'Remap sessions'
    };
  }

  private runRemapExistingSessions(): void {
    this.remapError = '';
    this.remapResult = null;
    this.showRemapErrors = false;
    this.successMessage = '';
    this.isRemapping = true;

    this.hotspotPassengerService.remapExistingSessions(this.normalizedMaintenanceLimit()).pipe(
      finalize(() => this.isRemapping = false)
    ).subscribe({
      next: result => {
        this.remapResult = result;
        this.successMessage = `Reviewed ${result.reviewed} sessions and remapped ${result.remappedSessions}.`;
      },
      error: error => {
        this.remapError = this.getErrorMessage(error) || 'Unable to remap existing sessions.';
      }
    });
  }

  relinkPassengerProfiles(): void {
    if (!this.isAdminOrPlatform || this.isRelinking) {
      return;
    }

    this.pendingHotspotAction = {
      type: 'relink',
      title: 'Relink passenger profiles',
      message: 'Relink existing sessions to passenger profiles now? Run passenger sync first for best results.',
      confirmLabel: 'Relink profiles'
    };
  }

  private runRelinkPassengerProfiles(): void {
    this.relinkError = '';
    this.relinkResult = null;
    this.showRelinkErrors = false;
    this.successMessage = '';
    this.isRelinking = true;

    this.hotspotPassengerService.relinkPassengerProfiles(this.normalizedMaintenanceLimit()).pipe(
      finalize(() => this.isRelinking = false)
    ).subscribe({
      next: result => {
        this.relinkResult = result;
        this.successMessage = `Reviewed ${result.reviewed} sessions and relinked ${result.relinkedSessions}.`;
      },
      error: error => {
        this.relinkError = this.getErrorMessage(error) || 'Unable to relink passenger profiles.';
      }
    });
  }

  loadSessionHistory(page = this.sessionHistoryPage, resource: '' | 'Passengers' | 'Sessions' = this.sessionHistoryResource): void {
    this.isLoadingSessionHistory = true;
    this.sessionHistoryError = '';
    this.sessionHistoryPage = page;
    this.sessionHistoryResource = resource;

    this.hotspotPassengerService.getSyncRuns(this.sessionHistoryResource || undefined, this.sessionHistoryPage, this.sessionHistoryPerPage).pipe(
      finalize(() => this.isLoadingSessionHistory = false)
    ).subscribe({
      next: response => {
        this.sessionHistory = response.data || [];
        this.sessionHistoryPage = Number(response.meta?.page || this.sessionHistoryPage);
        this.sessionHistoryTotalPages = Number(response.meta?.totalPages || 1);
      },
      error: error => {
        this.sessionHistory = [];
        this.sessionHistoryError = this.getErrorMessage(error) || 'Unable to load sync history.';
      }
    });
  }

  onHistoryResourceChanged(): void {
    this.loadSessionHistory(1, this.sessionHistoryResource);
  }

  changeSessionHistoryPage(page: number): void {
    if (page < 1 || page > this.sessionHistoryTotalPages || page === this.sessionHistoryPage) {
      return;
    }

    this.loadSessionHistory(page);
  }

  getPagedSessionResults(result: HotspotSessionSyncResponse): HotspotSessionSyncResultRow[] {
    const start = (this.sessionResultPage - 1) * this.sessionResultPageSize;
    return (result.results || []).slice(start, start + this.sessionResultPageSize);
  }

  getSessionResultTotalPages(result: HotspotSessionSyncResponse): number {
    return Math.max(1, Math.ceil((result.results?.length || 0) / this.sessionResultPageSize));
  }

  getSessionResultSummary(result: HotspotSessionSyncResponse): string {
    const total = result.results?.length || 0;
    if (!total) {
      return '0 results';
    }

    const start = ((this.sessionResultPage - 1) * this.sessionResultPageSize) + 1;
    const end = Math.min(this.sessionResultPage * this.sessionResultPageSize, total);
    return `${start}-${end} of ${total}`;
  }

  changeSessionResultPage(page: number, result: HotspotSessionSyncResponse): void {
    const totalPages = this.getSessionResultTotalPages(result);
    if (page < 1 || page > totalPages || page === this.sessionResultPage) {
      return;
    }

    this.sessionResultPage = page;
  }

  getSessionRunId(run: HotspotSyncRun): string {
    return String(run.hotspotSyncRunID || run.id || '-');
  }

  getSessionRunTime(run: HotspotSyncRun): string {
    return this.formatDate(run.completedAt || run.startedAt || run.createdAt);
  }

  trackSessionResult(_index: number, result: HotspotSessionSyncResultRow): string {
    return result.externalID;
  }

  trackSessionError(index: number, error: HotspotSessionSyncError): string {
    return String(error.externalID || index);
  }

  getErrorCount(errors?: HotspotSessionSyncError[] | null): number {
    return errors?.length || 0;
  }

  getRunErrorCount(run: HotspotSyncRun): number | string {
    return run.errorCount ?? run.errors ?? '-';
  }

  trackSessionRun(_index: number, run: HotspotSyncRun): string {
    return String(run.hotspotSyncRunID || run.id || _index);
  }

  getSyncMessage(result: HotspotPassengerSyncResult): string {
    if (result.createdProfile && result.createdExternalIdentity) {
      return 'New passenger profile created and linked to a Powerlynx identity.';
    }

    if (result.createdProfile) {
      return 'New passenger profile created.';
    }

    if (result.createdExternalIdentity) {
      return 'Existing passenger profile linked to a new Powerlynx identity.';
    }

    return 'Existing passenger profile updated.';
  }

  getStatusLabel(result: HotspotPassengerSyncResult): string {
    if (result.createdProfile) {
      return 'created';
    }

    if (result.createdExternalIdentity) {
      return 'linked';
    }

    return 'updated';
  }

  formatDate(value?: string): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  }

  trackPassenger(_index: number, passenger: HotspotPassenger): string {
    return passenger.id;
  }

  private getErrorMessage(error: any): string {
    return error?.error?.message
      || error?.error?.title
      || error?.message
      || 'Unable to sync this hotspot passenger profile.';
  }

  private selectPassengerFromLoadedList(): void {
    this.selectedPassenger = this.selectedPassengerId
      ? this.passengers.find(passenger => passenger.id === this.selectedPassengerId) || null
      : null;
  }

  private comparePassengersByPresence(first: HotspotPassenger, second: HotspotPassenger): number {
    const onlineCompare = this.getOnlineRank(second) - this.getOnlineRank(first);
    if (onlineCompare !== 0) {
      return onlineCompare;
    }

    const lastSeenCompare = this.getPassengerLastSeenTime(second) - this.getPassengerLastSeenTime(first);
    if (lastSeenCompare !== 0) {
      return lastSeenCompare;
    }

    return first.displayName.localeCompare(second.displayName);
  }

  private getOnlineRank(passenger: HotspotPassenger): number {
    return passenger.onlineState === 'Online' ? 1 : 0;
  }

  private getPassengerLastSeenTime(passenger: HotspotPassenger): number {
    const value = passenger.lastOnline || passenger.lastSeen;
    if (!value) {
      return 0;
    }

    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  private normalizedLimit(): number {
    return Math.max(1, Math.min(1000, Number(this.sessionLimit) || 500));
  }

  private normalizedOffset(): number {
    return Math.max(0, Number(this.sessionOffset) || 0);
  }

  private normalizedPassengerSyncPage(): number {
    return Math.max(1, Number(this.passengerSyncPage) || 1);
  }

  private normalizedPassengerSyncPerPage(): number {
    return Math.max(1, Math.min(1000, Number(this.passengerSyncPerPage) || 100));
  }

  private normalizedCatchUpLimit(): number {
    return Math.max(1, Math.min(1000, Number(this.catchUpLimit) || 500));
  }

  private normalizedCatchUpStartOffset(): number {
    return Math.max(0, Number(this.catchUpStartOffset) || 0);
  }

  private normalizedCatchUpMaxBatches(): number | null {
    const value = Number(this.catchUpMaxBatches);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : null;
  }

  private normalizedMaintenanceLimit(): number {
    return Math.max(1, Math.min(10000, Number(this.maintenanceLimit) || 1000));
  }

  private getCurrentRole(): UserRole {
    const currentUser = this.authService.getCurrentUser();
    return normalizeUserRole(
      currentUser?.userRoleName
      || currentUser?.userRole
      || currentUser?.userRoleID
      || UserRole.PASSENGER
    );
  }
}
